import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { accessibilityTools, ColorBlindnessType, ContrastResult } from '../lib/accessibilityTools';
import { AlertTriangle, CheckCircle, Download, Eye, Trash2, XCircle, Loader2 } from 'lucide-react';
import { InfoIconTooltip } from './Tooltip';
import { educationalContent } from '../lib/educationalContent';
import { useToolState } from '../contexts/ToolStateContext';
import { ColorInput } from './ui/ColorInput';
import type { SavedPair } from '../types/accessibility';

type TargetCompliance = 'aa' | 'aaa' | 'wcag22';
type A11yTab = 'contrast' | 'colorblindness';

const formatAlpha = (value: number) => `${Math.round(value * 100)}%`;

const meetsLargeText = (sizePx: number, isBold: boolean) => (isBold ? sizePx >= 18 : sizePx >= 24);

export function AccessibilityAuditor() {
  const { getToolState, setToolState } = useToolState();
  const persisted = getToolState('accessibility');

  const [foreground, setForeground] = useState(persisted?.foreground ?? '#000000');
  const [background, setBackground] = useState(persisted?.background ?? '#ffffff');
  const [contrastResult, setContrastResult] = useState<ContrastResult | null>(null);
  const [selectedSimulation, setSelectedSimulation] = useState<ColorBlindnessType>('protanopia');
  const [simulatedFg, setSimulatedFg] = useState<string | null>(null);
  const [simulatedBg, setSimulatedBg] = useState<string | null>(null);
  const [foregroundAlpha, setForegroundAlpha] = useState(persisted?.foregroundAlpha ?? 1);
  const [backgroundAlpha, setBackgroundAlpha] = useState(persisted?.backgroundAlpha ?? 1);
  const [textSize, setTextSize] = useState(persisted?.textSize ?? 16);
  const [isBold, setIsBold] = useState(persisted?.isBold ?? false);
  const [targetCompliance, setTargetCompliance] = useState<TargetCompliance>((persisted?.targetCompliance as TargetCompliance) ?? 'aa');
  const [savedPairs, setSavedPairs] = useState<SavedPair[]>(persisted?.savedPairs ?? []);

  // 2.1 — Tab state
  const [activeTab, setActiveTab] = useState<A11yTab>('contrast');

  // Persist state on change
  useEffect(() => {
    setToolState('accessibility', {
      foreground,
      background,
      foregroundAlpha,
      backgroundAlpha,
      textSize,
      isBold,
      targetCompliance,
      savedPairs,
    });
  }, [foreground, background, foregroundAlpha, backgroundAlpha, textSize, isBold, targetCompliance, savedPairs, setToolState]);

  // Debounced auto-check
  const [isDebouncing, setIsDebouncing] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkContrast = useCallback(() => {
    // Cancel any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setIsDebouncing(false);

    const result = accessibilityTools.calculateContrast(foreground, background, {
      foregroundAlpha,
      backgroundAlpha,
      textSizePx: textSize,
      isBold,
    });
    setContrastResult(result);

    if (!result) {
      return;
    }

    const largeText = meetsLargeText(textSize, isBold);
    const aaCompliant = largeText ? result.wcag.aa.largeText : result.wcag.aa.normalText;
    const aaaCompliant = largeText ? result.wcag.aaa.largeText : result.wcag.aaa.normalText;
    const wcag22Compliant = largeText ? result.wcag.twoPointTwo.largeText : result.wcag.twoPointTwo.smallText;

    let complianceAchieved = false;
    let complianceLabel = '';

    if (targetCompliance === 'aa' && aaCompliant) {
      complianceAchieved = true;
      complianceLabel = `WCAG 2.1 AA (${largeText ? 'Large Text' : 'Normal Text'})`;
    } else if (targetCompliance === 'aaa' && aaaCompliant) {
      complianceAchieved = true;
      complianceLabel = `WCAG 2.1 AAA (${largeText ? 'Large Text' : 'Normal Text'})`;
    } else if (targetCompliance === 'wcag22' && wcag22Compliant) {
      complianceAchieved = true;
      complianceLabel = `WCAG 2.2 (${largeText ? 'Large Text' : 'Small Text'})`;
    }

    if (complianceAchieved) {
      const pairId = `${result.effective.foreground}-${result.effective.background}-${textSize}-${isBold ? 'bold' : 'regular'}`;
      setSavedPairs((prev) => {
        if (prev.some((pair) => pair.id === pairId)) {
          return prev;
        }

        const next: SavedPair = {
          id: pairId,
          complianceLabel,
          ratio: result.ratio,
          originalForeground: foreground,
          originalBackground: background,
          effectiveForeground: result.effective.foreground,
          effectiveBackground: result.effective.background,
          foregroundAlpha,
          backgroundAlpha,
          textSizePx: textSize,
          isBold,
        };
        return [...prev, next].sort((a, b) => b.ratio - a.ratio);
      });
    }
  }, [foreground, background, foregroundAlpha, backgroundAlpha, textSize, isBold, targetCompliance]);

  // Auto-check with 300ms debounce whenever inputs change
  useEffect(() => {
    setIsDebouncing(true);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      checkContrast();
    }, 300);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [foreground, background, foregroundAlpha, backgroundAlpha, textSize, isBold, checkContrast]);

  const simulateColorBlindness = () => {
    const baseForeground = contrastResult?.effective.foreground ?? foreground;
    const baseBackground = contrastResult?.effective.background ?? background;
    const simFg = accessibilityTools.simulateColorBlindness(baseForeground, selectedSimulation);
    const simBg = accessibilityTools.simulateColorBlindness(baseBackground, selectedSimulation);
    setSimulatedFg(simFg);
    setSimulatedBg(simBg);
  };

  // Auto-trigger simulation when dropdown or inputs change (only if already active)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (simulatedFg !== null && simulatedBg !== null) {
      simulateColorBlindness();
    }
  }, [selectedSimulation, foreground, background]);

  const removeSavedPair = (id: string) => {
    setSavedPairs((prev) => prev.filter((pair) => pair.id !== id));
  };

  const exportSavedPairs = () => {
    if (!savedPairs.length) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const blob = new Blob([JSON.stringify(savedPairs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'accessible-color-pairs.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const largeText = useMemo(() => meetsLargeText(textSize, isBold), [textSize, isBold]);

  const simulationTypes: Array<{ value: ColorBlindnessType; label: string }> = [
    { value: 'protanopia', label: 'Protanopia (Red-blind)' },
    { value: 'deuteranopia', label: 'Deuteranopia (Green-blind)' },
    { value: 'tritanopia', label: 'Tritanopia (Blue-blind)' },
    { value: 'protanomaly', label: 'Protanomaly (Red-weak)' },
    { value: 'deuteranomaly', label: 'Deuteranomaly (Green-weak)' },
    { value: 'tritanomaly', label: 'Tritanomaly (Blue-weak)' },
    { value: 'achromatopsia', label: 'Achromatopsia (No color)' },
    { value: 'achromatomaly', label: 'Achromatomaly (Weak color)' },
  ];

  return (
    <div className="w-full max-w-6xl mx-auto p-6 space-y-6">
      {/* 2.2 — Shared inputs: FG/BG ColorInputs + opacity sliders (always visible) */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <ColorInput
              value={foreground}
              onChange={setForeground}
              compact
              label="Foreground Color"
              placeholder="e.g., #000000"
              onSelectFromGlobal
            />
            <div className="mt-3">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center justify-between">
                <span>Foreground Opacity</span>
                <span className="font-mono">{formatAlpha(foregroundAlpha)}</span>
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={foregroundAlpha}
                onChange={(e) => setForegroundAlpha(parseFloat(e.target.value))}
                className="w-full accent-blue-600"
              />
            </div>
          </div>

          <div>
            <ColorInput
              value={background}
              onChange={setBackground}
              compact
              label="Background Color"
              placeholder="e.g., #ffffff"
              onSelectFromGlobal
            />
            <div className="mt-3">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center justify-between">
                <span>Background Opacity</span>
                <span className="font-mono">{formatAlpha(backgroundAlpha)}</span>
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={backgroundAlpha}
                onChange={(e) => setBackgroundAlpha(parseFloat(e.target.value))}
                className="w-full accent-blue-600"
              />
            </div>
          </div>
        </div>

        {/* 2.3 — Typography configuration (always visible above tabs) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Text Size</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={10}
                max={64}
                value={textSize}
                onChange={(e) => setTextSize(parseInt(e.target.value, 10))}
                className="flex-1 accent-blue-600"
              />
              <span className="w-12 text-right text-sm font-mono text-gray-700 dark:text-gray-300">{textSize}px</span>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {largeText
                ? 'Counts as large text for WCAG thresholds.'
                : 'Counts as normal text for WCAG thresholds.'}
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Typeface Weight</label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={isBold}
                onChange={(e) => setIsBold(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Bold (700+)</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Toggle to evaluate bold body copy against WCAG large-text thresholds.
            </p>
          </div>

          <div>
            <label className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Target Compliance
              <InfoIconTooltip
                content={educationalContent.wcagAAA.description}
                learnMoreUrl={educationalContent.wcagAAA.learnMoreUrl}
                position="right"
              />
            </label>
            <select
              value={targetCompliance}
              onChange={(e) => setTargetCompliance(e.target.value as TargetCompliance)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="aa">WCAG 2.1 AA</option>
              <option value="aaa">WCAG 2.1 AAA</option>
              <option value="wcag22">WCAG 2.2</option>
            </select>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              ✨ Pairs meeting this level are captured automatically below.
            </p>
          </div>
        </div>

        {/* 2.4 — Tab bar */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('contrast')}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === 'contrast'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Contrast Checker
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('colorblindness')}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === 'colorblindness'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Color Blindness
          </button>
        </div>

        {/* 2.5 — Contrast Checker tab content */}
        {activeTab === 'contrast' && (
          <div>
            {/* Debounce spinner + manual check */}
            <div className="flex items-center gap-3 mb-6">
              <button
                type="button"
                onClick={checkContrast}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                title="Re-check contrast immediately (auto-checks after 300ms)"
              >
                Re-check Now
              </button>
              {isDebouncing && (
                <span className="inline-flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                  <Loader2 className="w-4 h-4 animate-spin" /> Checking…
                </span>
              )}
            </div>

            {/* Results */}
            {contrastResult && (
              <div className="mb-6 space-y-4">
                <div className="p-6 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                        {contrastResult.ratio.toFixed(2)}:1
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Contrast Ratio</p>
                    </div>
                    <div
                      className="w-32 h-32 rounded-lg shadow-lg flex items-center justify-center border-2 border-gray-200 dark:border-gray-700"
                      style={{
                        backgroundColor: contrastResult.effective.background,
                        color: contrastResult.effective.foreground,
                        fontSize: `${textSize}px`,
                        fontWeight: isBold ? 700 : 400,
                      }}
                    >
                      <span>Sample</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 text-sm text-gray-700 dark:text-gray-300">
                    <div>
                      <h5 className="font-semibold mb-2">Effective Colors</h5>
                      <p>
                        Foreground: <span className="font-mono">{contrastResult.effective.foreground}</span> ({formatAlpha(contrastResult.effective.foregroundAlpha)})
                      </p>
                      <p>
                        Background: <span className="font-mono">{contrastResult.effective.background}</span> ({formatAlpha(contrastResult.effective.backgroundAlpha)})
                      </p>
                    </div>
                    <div>
                      <h5 className="font-semibold mb-2">Typography</h5>
                      <p>Size: {textSize}px ({largeText ? 'Large text' : 'Normal text'})</p>
                      <p>Weight: {isBold ? 'Bold / 700' : 'Regular / 400'}</p>
                      <p>Target: {targetCompliance.toUpperCase().replace('WCAG', 'WCAG ')}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">WCAG AA</h4>
                      <div className="flex items-center gap-2">
                        {contrastResult.wcag.aa.normalText ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300">Normal Text (4.5:1)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {contrastResult.wcag.aa.largeText ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300">Large Text (3:1)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {contrastResult.wcag.aa.ui ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300">UI Components (3:1)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {contrastResult.wcag.aa.focusIndicator ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300">Focus Indicator (3:1)</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">WCAG AAA</h4>
                      <div className="flex items-center gap-2">
                        {contrastResult.wcag.aaa.normalText ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300">Normal Text (7:1)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {contrastResult.wcag.aaa.largeText ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300">Large Text (4.5:1)</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">WCAG 2.2</h4>
                      <div className="flex items-center gap-2">
                        {contrastResult.wcag.twoPointTwo.smallText ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300">Small Text (4.5:1)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {contrastResult.wcag.twoPointTwo.largeText ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300">Large Text (3:1)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {contrastResult.wcag.twoPointTwo.enhancedFocusIndicator ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300">Focus Indicator (3:1)</span>
                      </div>
                    </div>
                  </div>

                  {contrastResult.suggestions && (
                    <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h5 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">Suggestions</h5>
                          <div className="space-y-1 text-sm">
                            <p className="text-yellow-800 dark:text-yellow-200">
                              Lighten:{' '}
                              <button
                                type="button"
                                onClick={() => setForeground(contrastResult.suggestions!.lighten)}
                                className="font-mono underline hover:text-yellow-600 dark:hover:text-yellow-100 cursor-pointer"
                                title="Apply lighter foreground"
                                aria-label={`Apply lighter foreground ${contrastResult.suggestions.lighten}`}
                              >
                                {contrastResult.suggestions.lighten}
                              </button>
                            </p>
                            <p className="text-yellow-800 dark:text-yellow-200">
                              Darken:{' '}
                              <button
                                type="button"
                                onClick={() => setForeground(contrastResult.suggestions!.darken)}
                                className="font-mono underline hover:text-yellow-600 dark:hover:text-yellow-100 cursor-pointer"
                                title="Apply darker foreground"
                                aria-label={`Apply darker foreground ${contrastResult.suggestions.darken}`}
                              >
                                {contrastResult.suggestions.darken}
                              </button>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 2.6 — Color Blindness tab content */}
        {activeTab === 'colorblindness' && (
          <div>
            <div className="space-y-4 mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Simulation Type
              </label>
              <select
                value={selectedSimulation}
                onChange={(e) => setSelectedSimulation(e.target.value as ColorBlindnessType)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                {simulationTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={simulateColorBlindness}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
              >
                <Eye className="w-5 h-5" />
                Simulate
              </button>
            </div>

            {simulatedFg && simulatedBg && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Original</h4>
                  <div
                    className="w-full h-48 rounded-lg shadow-lg flex items-center justify-center border-2 border-gray-200 dark:border-gray-700"
                    style={{
                      backgroundColor: background,
                      color: foreground,
                      fontSize: `${textSize}px`,
                      fontWeight: isBold ? 700 : 400,
                    }}
                  >
                    <div className="text-center">
                      <p className="text-2xl font-bold mb-2">Sample Text</p>
                      <p className="text-sm opacity-75">The quick brown fox</p>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <p>Foreground: {foreground}</p>
                    <p>Background: {background}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Simulated</h4>
                  <div
                    className="w-full h-48 rounded-lg shadow-lg flex items-center justify-center border-2 border-gray-200 dark:border-gray-700"
                    style={{
                      backgroundColor: simulatedBg ?? background,
                      color: simulatedFg ?? foreground,
                      fontSize: `${textSize}px`,
                      fontWeight: isBold ? 700 : 400,
                    }}
                  >
                    <div className="text-center">
                      <p className="text-2xl font-bold mb-2">Sample Text</p>
                      <p className="text-sm opacity-75">The quick brown fox</p>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <p>Foreground: {simulatedFg}</p>
                    <p>Background: {simulatedBg}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 2.7 — Saved Pairs section (always visible below tabs) */}
      {savedPairs.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Saved Compliant Pairs</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Automatically captured when a combination meets your selected WCAG level.</p>
            </div>
            <button
              type="button"
              onClick={exportSavedPairs}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Download className="w-4 h-4" /> Export JSON
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {savedPairs.map((pair) => (
              <div
                key={pair.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900 space-y-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{pair.complianceLabel}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Contrast {pair.ratio.toFixed(2)}:1</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSavedPair(pair.id)}
                    className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </div>
                <div
                  className="rounded-md border border-gray-200 dark:border-gray-700 p-3 flex flex-wrap items-center justify-between gap-2 relative group"
                  style={{
                    backgroundColor: pair.effectiveBackground,
                    color: pair.effectiveForeground,
                    fontSize: `${pair.textSizePx}px`,
                    fontWeight: pair.isBold ? 700 : 400,
                    minHeight: '72px',
                  }}
                >
                  <span className="text-sm font-medium">Accessible Sample</span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      className="px-2 py-1 text-xs rounded bg-black/30 hover:bg-black/50 text-white"
                      onClick={() => setForeground(pair.effectiveForeground)}
                      aria-label="Set as foreground"
                      title="Set this pair's foreground"
                    >
                      FG
                    </button>
                    <button
                      type="button"
                      className="px-2 py-1 text-xs rounded bg-black/30 hover:bg-black/50 text-white"
                      onClick={() => {
                        setForeground(pair.effectiveForeground);
                        setBackground(pair.effectiveBackground);
                      }}
                      aria-label="Set both foreground and background"
                      title="Set both FG and BG from this pair"
                    >
                      Both
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs text-gray-700 dark:text-gray-300">
                  <div
                    className="cursor-pointer hover:text-blue-600"
                    onClick={() => setForeground(pair.effectiveForeground)}
                    title="Click to set as foreground"
                  >
                    <p className="font-semibold text-gray-600 dark:text-gray-400 mb-1">Original <span className="text-blue-500 font-normal">→ set FG</span></p>
                    <p>FG: <span className="font-mono">{pair.originalForeground}</span></p>
                    <p>BG: <span className="font-mono">{pair.originalBackground}</span></p>
                  </div>
                  <div
                    className="cursor-pointer hover:text-blue-600"
                    onClick={() => setBackground(pair.effectiveBackground)}
                    title="Click to set as background"
                  >
                    <p className="font-semibold text-gray-600 dark:text-gray-400 mb-1">Effective <span className="text-blue-500 font-normal">→ set BG</span></p>
                    <p>FG: <span className="font-mono">{pair.effectiveForeground}</span> ({formatAlpha(pair.foregroundAlpha)})</p>
                    <p>BG: <span className="font-mono">{pair.effectiveBackground}</span> ({formatAlpha(pair.backgroundAlpha)})</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-600 dark:text-gray-400 mb-1">Typography</p>
                    <p>Size: {pair.textSizePx}px</p>
                    <p>Weight: {pair.isBold ? 'Bold' : 'Regular'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
